import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle, MoreVertical, Save, Copy, Edit2, Trash2, X, Download, Upload, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';
import { getUserSubscription } from '../lib/subscription';

interface Project {
  id: string;
  name: string;
  is_template_edit?: boolean;
}

interface Template {
  id: string;
  name: string;
}

interface ExportedTodo {
  text: string;
  notes?: string;
  priority?: boolean;
  color?: 'yellow' | 'green' | 'blue' | null;
  deadline?: string;
}

interface ProjectSelectorProps {
  projects: Project[];
  selectedProject: string | null;
  onProjectChange: (projectId: string) => void;
  onAddProject: (name: string) => void;
  onRefresh?: () => void | Promise<void>;
}

export function ProjectSelector({
  projects,
  selectedProject,
  onProjectChange,
  onAddProject,
  onRefresh,
}: ProjectSelectorProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isAddingProjectMobile, setIsAddingProjectMobile] = useState(false);
  const [showMainMenu, setShowMainMenu] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isNamingTemplate, setIsNamingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateMenus, setTemplateMenus] = useState<{ [key: string]: boolean }>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showTaskLimitError, setShowTaskLimitError] = useState(false);
  const [taskLimitErrorMessage, setTaskLimitErrorMessage] = useState('');
  const [editingProjectName, setEditingProjectName] = useState<{ id: string; name: string } | null>(null);
  const [showPremiumFeatureModal, setShowPremiumFeatureModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mainMenuRef = useRef<HTMLDivElement>(null);
  const mainMenuButtonRef = useRef<HTMLButtonElement>(null);
  const templateMenusRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const templateButtonsRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMainMenu &&
          mainMenuRef.current &&
          mainMenuButtonRef.current &&
          !mainMenuRef.current.contains(event.target as Node) &&
          !mainMenuButtonRef.current.contains(event.target as Node)) {
        setShowMainMenu(false);
      }

      Object.entries(templateMenus).forEach(([templateId, isOpen]) => {
        if (isOpen &&
            templateMenusRef.current[templateId] &&
            templateButtonsRef.current[templateId] &&
            !templateMenusRef.current[templateId]?.contains(event.target as Node) &&
            !templateButtonsRef.current[templateId]?.contains(event.target as Node)) {
          setTemplateMenus(prev => ({ ...prev, [templateId]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMainMenu, templateMenus]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleAddProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    onAddProject(newProjectName.trim());
    setNewProjectName('');
    setIsAddingProject(false);
    setIsAddingProjectMobile(false);
  };

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      // keep the spin visible briefly so the action is perceivable
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  const toggleMainMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMainMenu(!showMainMenu);
    setTemplateMenus({});
  };

  const toggleTemplateMenu = (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    setTemplateMenus(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const deleteProject = async () => {
    if (!selectedProject || !user) return;

    try {
      const { error: todosError } = await supabase
        .from('todos')
        .delete()
        .eq('project_id', selectedProject);

      if (todosError) throw todosError;

      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', selectedProject);

      if (projectError) throw projectError;

      window.location.reload();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const startRenamingProject = () => {
    if (!selectedProject) return;

    const project = projects.find(p => p.id === selectedProject);
    if (!project) return;

    setEditingProjectName({ id: project.id, name: project.name });
    setShowMainMenu(false);
  };

  const updateProjectName = async (id: string, newName: string) => {
    if (!user || !newName.trim()) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ name: newName.trim() })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Refresh the page to update the project list
      window.location.reload();
    } catch (error) {
      console.error('Error updating project name:', error);
      setEditingProjectName(null);
    }
  };

  const saveAsTemplate = async () => {
    if (!selectedProject || !user || !newTemplateName.trim()) return;

    try {
      // First, fetch todos from current project
      const { data: todos, error: todosError } = await supabase
        .from('todos')
        .select('text, notes')
        .eq('project_id', selectedProject);

      if (todosError) throw todosError;

      // Check task limit before creating template
      if (todos && todos.length > 0) {
        const subscription = await getUserSubscription();

        if (subscription) {
          const wouldExceedLimit = subscription.currentTaskCount + todos.length > subscription.taskLimit;

          if (wouldExceedLimit) {
            const message = subscription.subscriptionTier === 'free'
              ? `You have ${subscription.currentTaskCount}/${subscription.taskLimit} tasks. This template has ${todos.length} tasks which would exceed your limit. Upgrade to Premium for up to 1000 tasks.`
              : `You have ${subscription.currentTaskCount}/${subscription.taskLimit} tasks. This template has ${todos.length} tasks which would exceed your limit.`;

            setTaskLimitErrorMessage(message);
            setShowTaskLimitError(true);
            setIsNamingTemplate(false);
            setNewTemplateName('');
            return;
          }
        }
      }

      // If within limit, proceed with creating template
      const { data: templateData, error: templateError } = await supabase
        .from('project_templates')
        .insert([{
          name: newTemplateName.trim(),
          user_id: user.id
        }])
        .select()
        .single();

      if (templateError) throw templateError;

      if (todos && todos.length > 0) {
        const templateTodos = todos.map(todo => ({
          template_id: templateData.id,
          text: todo.text,
          notes: todo.notes,
          user_id: user.id
        }));

        const { error: insertError } = await supabase
          .from('template_todos')
          .insert(templateTodos);

        if (insertError) throw insertError;
      }

      await fetchTemplates();
      setIsNamingTemplate(false);
      setNewTemplateName('');
      setShowMainMenu(false);
    } catch (error) {
      console.error('Error saving template:', error);
      // Close the dialog on any error to prevent UI from hanging
      setIsNamingTemplate(false);
      setNewTemplateName('');
    }
  };

  const copyFromTemplate = async (templateId: string) => {
    if (!selectedProject || !user) return;

    try {
      // Get template todos first
      const { data: templateTodos, error: todosError } = await supabase
        .from('template_todos')
        .select('*')
        .eq('template_id', templateId);

      if (todosError) throw todosError;

      if (templateTodos && templateTodos.length > 0) {
        // Check task limit before copying
        const subscription = await getUserSubscription();

        if (subscription) {
          const wouldExceedLimit = subscription.currentTaskCount + templateTodos.length > subscription.taskLimit;

          if (wouldExceedLimit) {
            const message = subscription.subscriptionTier === 'free'
              ? `You have ${subscription.currentTaskCount}/${subscription.taskLimit} tasks. This template has ${templateTodos.length} tasks which would exceed your limit. Upgrade to Premium for up to 1000 tasks.`
              : `You have ${subscription.currentTaskCount}/${subscription.taskLimit} tasks. This template has ${templateTodos.length} tasks which would exceed your limit.`;

            setTaskLimitErrorMessage(message);
            setShowTaskLimitError(true);
            setTemplateMenus({});
            setShowMainMenu(false);
            return;
          }
        }

        // If within limit, proceed with copying
        const projectTodos = templateTodos.map(todo => ({
          project_id: selectedProject,
          user_id: user.id,
          text: todo.text,
          notes: todo.notes,
          status: 'backlog',
          priority: false,
          color: null
        }));

        const { error: insertError } = await supabase
          .from('todos')
          .insert(projectTodos);

        if (insertError) throw insertError;
      }

      setTemplateMenus({});
      setShowMainMenu(false);

      const event = new CustomEvent('refreshTodos');
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error copying from template:', error);
    }
  };

  const startEditingTemplate = async (templateId: string) => {
    if (!user) return;
    
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) return;

      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .insert([{ 
          name: template.name,
          user_id: user.id,
          is_template_edit: true
        }])
        .select()
        .single();

      if (projectError) throw projectError;

      const { data: templateTodos, error: todosError } = await supabase
        .from('template_todos')
        .select('*')
        .eq('template_id', templateId);

      if (todosError) throw todosError;

      if (templateTodos && templateTodos.length > 0) {
        const projectTodos = templateTodos.map(todo => ({
          project_id: projectData.id,
          user_id: user.id,
          text: todo.text,
          notes: todo.notes,
          status: 'backlog',
          priority: false,
          color: null
        }));

        const { error: insertError } = await supabase
          .from('todos')
          .insert(projectTodos);

        if (insertError) throw insertError;
      }

      setEditingTemplate(template);
      onProjectChange(projectData.id);
      setTemplateMenus({});
      setShowMainMenu(false);
    } catch (error) {
      console.error('Error starting template edit:', error);
    }
  };

  const updateTemplate = async () => {
    if (!selectedProject || !user || !editingTemplate) return;

    try {
      const { error: deleteError } = await supabase
        .from('template_todos')
        .delete()
        .eq('template_id', editingTemplate.id);

      if (deleteError) throw deleteError;

      const { data: todos, error: todosError } = await supabase
        .from('todos')
        .select('text, notes')
        .eq('project_id', selectedProject);

      if (todosError) throw todosError;

      if (todos && todos.length > 0) {
        const templateTodos = todos.map(todo => ({
          template_id: editingTemplate.id,
          text: todo.text,
          notes: todo.notes,
          user_id: user.id
        }));

        const { error: insertError } = await supabase
          .from('template_todos')
          .insert(templateTodos);

        if (insertError) throw insertError;
      }

      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', selectedProject);

      if (projectError) throw projectError;

      window.location.reload();
    } catch (error) {
      console.error('Error updating template:', error);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('project_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      await fetchTemplates();
      setTemplateMenus({});
      setShowMainMenu(false);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const exportTodosToJson = async () => {
    if (!selectedProject || !user) return;

    try {
      // Check if user is premium
      const subscription = await getUserSubscription();
      if (subscription && subscription.subscriptionTier !== 'premium') {
        setShowPremiumFeatureModal(true);
        setShowMainMenu(false);
        return;
      }

      // Fetch all todos from the project
      const { data: todos, error } = await supabase
        .from('todos')
        .select('text, notes, priority, color, deadline')
        .eq('project_id', selectedProject);

      if (error) throw error;

      if (!todos || todos.length === 0) {
        alert('No tasks to export');
        return;
      }

      // Create JSON structure
      const exportData = todos.map(todo => ({
        text: todo.text,
        notes: todo.notes || undefined,
        priority: todo.priority || undefined,
        color: todo.color || undefined,
        deadline: todo.deadline || undefined
      }));

      // Create and download the file
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `tasks-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setShowMainMenu(false);
    } catch (error) {
      console.error('Error exporting tasks:', error);
      alert('Failed to export tasks. Please try again.');
    }
  };

  const importTodosFromJson = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user || !selectedProject) return;

    try {
      // Check if user is premium
      const subscription = await getUserSubscription();
      if (subscription && subscription.subscriptionTier !== 'premium') {
        setShowPremiumFeatureModal(true);
        setShowMainMenu(false);
        return;
      }

      // Read the file
      const text = await file.text();
      let importedTodos: ExportedTodo[];

      try {
        importedTodos = JSON.parse(text);
      } catch (parseError) {
        alert('Invalid JSON file format');
        return;
      }

      // Validate the structure
      if (!Array.isArray(importedTodos)) {
        alert('Invalid JSON format: expected an array of tasks');
        return;
      }

      // Check task limit before importing
      const subscriptionCheck = await getUserSubscription();
      if (subscriptionCheck) {
        const wouldExceedLimit = subscriptionCheck.currentTaskCount + importedTodos.length > subscriptionCheck.taskLimit;

        if (wouldExceedLimit) {
          const message = subscriptionCheck.subscriptionTier === 'free'
            ? `You have ${subscriptionCheck.currentTaskCount}/${subscriptionCheck.taskLimit} tasks. Importing ${importedTodos.length} tasks would exceed your limit. Upgrade to Premium for up to 1000 tasks.`
            : `You have ${subscriptionCheck.currentTaskCount}/${subscriptionCheck.taskLimit} tasks. Importing ${importedTodos.length} tasks would exceed your limit.`;

          setTaskLimitErrorMessage(message);
          setShowTaskLimitError(true);
          setShowMainMenu(false);
          return;
        }
      }

      // Import todos to backlog
      const todosToInsert = importedTodos.map(todo => ({
        project_id: selectedProject,
        user_id: user.id,
        text: todo.text || 'Untitled task',
        notes: todo.notes || '',
        status: 'backlog' as const,
        priority: todo.priority || false,
        color: todo.color || null,
        deadline: todo.deadline || null,
        position: Date.now() + Math.random() * 1000
      }));

      const { error } = await supabase
        .from('todos')
        .insert(todosToInsert);

      if (error) throw error;

      // Refresh the todo list
      const refreshEvent = new CustomEvent('refreshTodos');
      window.dispatchEvent(refreshEvent);

      alert(`Successfully imported ${importedTodos.length} tasks`);
      setShowMainMenu(false);
    } catch (error) {
      console.error('Error importing tasks:', error);
      alert('Failed to import tasks. Please try again.');
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const visibleProjects = projects.filter(project => !project.is_template_edit);

  return (
    <div className="flex items-center space-x-2 relative">
      {editingProjectName?.id === selectedProject ? (
        <input
          type="text"
          value={editingProjectName.name}
          onChange={(e) => setEditingProjectName({ ...editingProjectName, name: e.target.value })}
          onBlur={() => updateProjectName(editingProjectName.id, editingProjectName.name)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateProjectName(editingProjectName.id, editingProjectName.name);
            } else if (e.key === 'Escape') {
              setEditingProjectName(null);
            }
          }}
          className="flex-1 max-w-xs px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 md:max-w-xs w-full"
          autoFocus
        />
      ) : (
        <select
          value={selectedProject || ''}
          onChange={(e) => onProjectChange(e.target.value)}
          className="flex-1 max-w-xs px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 md:max-w-xs w-full"
        >
          <option value="" disabled>{t('selectProject')}</option>
          {editingTemplate ? (
            <option value={selectedProject || ''}>
              {editingTemplate.name}
            </option>
          ) : (
            visibleProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))
          )}
        </select>
      )}

      {isAddingProject ? (
        <form onSubmit={handleAddProject} className="flex-1 flex space-x-2 md:flex-row flex-col md:space-y-0 space-y-2">
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder={t('projectName')}
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full"
            autoFocus
          />
          <div className="flex space-x-2">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAddingProject(false);
                setIsAddingProjectMobile(false);
              }}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex items-center space-x-2">
          {editingTemplate ? (
            <button
              onClick={updateTemplate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 w-full md:w-auto"
            >
              Update template
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setIsAddingProject(true);
                  setIsAddingProjectMobile(true);
                }}
                className="p-2 text-indigo-600 hover:text-indigo-700 focus:outline-none"
                title={t('addNewProject')}
              >
                <PlusCircle className="h-6 w-6" />
              </button>
              {onRefresh && (
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-2 text-indigo-600 hover:text-indigo-700 focus:outline-none disabled:opacity-50"
                  title={t('refreshTasks')}
                >
                  <RefreshCw className={`h-6 w-6 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
              <div className="relative">
                <button
                  ref={mainMenuButtonRef}
                  onClick={toggleMainMenu}
                  className="p-2 text-indigo-600 hover:text-indigo-700 focus:outline-none"
                  title={t('moreOptions')}
                >
                  <MoreVertical className="h-6 w-6" />
                </button>

                {showMainMenu && (
                  <div
                    ref={mainMenuRef}
                    className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg py-2 z-20 min-w-[200px] w-full md:w-auto md:right-0 md:left-auto"
                  >
                    <button
                      onClick={startRenamingProject}
                      disabled={!selectedProject}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      {t('renameProject')}
                    </button>

                    <button
                      onClick={() => {
                        const selectedProjectData = projects.find(p => p.id === selectedProject);
                        if (selectedProjectData) {
                          setNewTemplateName(selectedProjectData.name);
                          setIsNamingTemplate(true);
                          setShowMainMenu(false);
                        }
                      }}
                      disabled={!selectedProject}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {t('saveAsTemplate')}
                    </button>

                    <button
                      onClick={exportTodosToJson}
                      disabled={!selectedProject}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      JSON Export
                    </button>

                    <button
                      onClick={handleImportClick}
                      disabled={!selectedProject}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      JSON Import
                    </button>

                    <button
                      onClick={() => {
                        if (selectedProject) {
                          setShowDeleteConfirm(true);
                          setShowMainMenu(false);
                        }
                      }}
                      disabled={!selectedProject}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('deleteProject')}
                    </button>
                    
                    {templates.length > 0 && (
                      <div className="border-t my-2">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                          {t('templates')}
                        </div>
                        {templates.map((template) => (
                          <div key={template.id} className="relative">
                            <div className="px-4 py-2 text-sm text-gray-700 flex items-center justify-between hover:bg-gray-50">
                              <span>{template.name}</span>
                              <button
                                ref={el => templateButtonsRef.current[template.id] = el}
                                onClick={(e) => toggleTemplateMenu(e, template.id)}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>
                            </div>
                            {templateMenus[template.id] && (
                              <div 
                                ref={el => templateMenusRef.current[template.id] = el}
                                className="absolute md:left-full md:top-0 md:ml-2 bottom-full right-0 mb-2 bg-white rounded-lg shadow-lg py-2 min-w-[160px] z-30 w-full md:w-auto h-fit"
                              >
                                <button
                                  onClick={() => copyFromTemplate(template.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  {t('copyFromTemplate')}
                                </button>
                                <button
                                  onClick={() => startEditingTemplate(template.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                >
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  {t('editTemplate')}
                                </button>
                                <button
                                  onClick={() => deleteTemplate(template.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {t('deleteTemplate')}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {isNamingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4 px-4">
              <h3 className="text-lg font-semibold">{t('saveAsTemplateTitle')}</h3>
              <button
                onClick={() => {
                  setIsNamingTemplate(false);
                  setNewTemplateName('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form className="px-4" onSubmit={(e) => {
              e.preventDefault();
              saveAsTemplate();
            }}>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder={t('templateName')}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4"
                autoFocus
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsNamingTemplate(false);
                    setNewTemplateName('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={!newTemplateName.trim()}
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="mb-4 px-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('deleteConfirmTitle')}</h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('deleteConfirmMessage')}
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  deleteProject();
                  setShowDeleteConfirm(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTaskLimitError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="mb-4 px-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('taskLimitExceeded')}</h3>
              <p className="mt-2 text-sm text-gray-700">
                {taskLimitErrorMessage}
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-4">
              <button
                onClick={() => setShowTaskLimitError(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('close')}
              </button>
              {taskLimitErrorMessage.includes('Upgrade to Premium') && (
                <button
                  onClick={() => {
                    setShowTaskLimitError(false);
                    // Dispatch custom event to open account settings
                    window.dispatchEvent(new CustomEvent('openAccountSettings'));
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {t('upgradeToPremium')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={importTodosFromJson}
        style={{ display: 'none' }}
      />

      {showPremiumFeatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                {t('premiumFeature')}
              </h3>
              <button
                onClick={() => setShowPremiumFeatureModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="bg-amber-50 p-4 rounded-md text-amber-800 mb-4">
                <p className="font-medium">{t('premiumFeature')}</p>
                <p className="text-sm mt-1">
                  {t('jsonExportImportPremiumOnly')}
                </p>
              </div>
            </div>
            <div className="flex justify-end px-6 pb-4">
              <button
                onClick={() => setShowPremiumFeatureModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
